import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss/lib/injectSheet';
import cn from 'classnames';
import theme from '../../styles/theme';
import CheckedIcon from '../../icons/CheckedIcon';
import CrossIcon from '../../icons/CrossIcon';
import { TOAST_ANIMATION_TIME, ToastType } from './constants';

const mapTypeToCloseClassname = {
  [ToastType.NEUTRAL]: 'closeButtonNeutral',
  [ToastType.POSITIVE]: 'closeButtonPositive',
  [ToastType.NEGATIVE]: 'closeButtonNegative',
};

const Toast = ({
  classes,
  type,
  title,
  description,
  icon,
  showIcon,
  action,
  actionHref,
  onActionClick,
  onDismiss,
  dismissible,
  autoHide,
  autoHideDelay,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleDismiss = useCallback(
    () => {
      if (!onDismiss) {
        return undefined;
      }

      setIsClosing(true);
      const timer = setTimeout(() => {
        onDismiss();
        // Don't wait for the animation complete to remove the toast, it will cause flashing.
      }, TOAST_ANIMATION_TIME - (TOAST_ANIMATION_TIME / 10));

      return () => clearTimeout(timer);
    },
    [],
  );

  useEffect(() => {
    if (autoHide && handleDismiss) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoHideDelay - TOAST_ANIMATION_TIME);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoHide, handleDismiss]);

  const iconElement = useMemo(() => {
    if (!showIcon) {
      return null;
    }

    if (icon) {
      return icon;
    }

    return <CheckedIcon className={classes.icon} />;
  }, [icon, type, showIcon]);

  const actionComponent = useMemo(
    () => {
      if (!action) {
        return null;
      }

      if (actionHref) {
        return (
          <a href={actionHref} className={classes.actionButton}>{action}</a>
        );
      }

      return (
        <button
          type="button"
          className={classes.actionButton}
          onClick={onActionClick}
        >
          {action}
        </button>
      );
    },
    [action, actionHref, onActionClick],
  );

  const closeComponent = useMemo(
    () => {
      if (!dismissible) {
        return null;
      }

      return (
        <button
          type="button"
          className={cn(
            classes.closeButton,
            classes[mapTypeToCloseClassname[type]],
          )}
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          <CrossIcon />
        </button>
      );
    },
    [dismissible, handleDismiss, type, classes],
  );

  return (
    <div
      className={cn(
        classes.toast,
        isClosing ? classes.toastDisappear : classes.toastAppear,
        classes[type],
      )}
      role="alert"
      aria-live="polite"
    >
      {iconElement && (
        <div className={classes.iconWrapper}>
          {iconElement}
        </div>
      )}

      <div className={classes.mainContentWrapper}>
        <div className={classes.titleWrapper}>
          <div className={classes.title}>{title}</div>
          {actionComponent}
        </div>

        {description && (
          <div className={cn(classes.description)}>
            {description}
          </div>
        )}
      </div>

      {closeComponent}
    </div>
  );
};

Toast.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  type: PropTypes.oneOf(Object.values(ToastType)).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  icon: PropTypes.node,
  action: PropTypes.string,
  actionHref: PropTypes.string,
  onActionClick: PropTypes.func,
  onDismiss: PropTypes.func,
  dismissible: PropTypes.bool,
  autoHide: PropTypes.bool,
  autoHideDelay: PropTypes.number,
  showIcon: PropTypes.bool,
};

Toast.defaultProps = {
  description: null,
  icon: null,
  action: null,
  actionHref: null,
  onActionClick: null,
  onDismiss: null,
  dismissible: true,
  autoHide: true,
  autoHideDelay: 10_000,
  showIcon: true,
};

/**
 * @type {Object.<string, React.CSSProperties>}
 */
const styles = {
  toast: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 4,
    fontFamily: theme.fonts.body,
    animationDuration: `${TOAST_ANIMATION_TIME}ms`,
    boxSizing: 'border-box',
    alignItems: 'flex-start',
    width: '100%',
    columnGap: 8,

    [theme.breakpoints.up('md')]: {
      width: 'fit-content',
      minWidth: 343,
      maxWidth: 600,
    },
  },
  toastAppear: {
    animationName: 'appear',
  },
  toastDisappear: {
    animationName: 'disappear',
  },
  iconWrapper: {
    flexShrink: 0,
    width: 24,
    height: 24,
    margin: 12,
    marginRight: 0,
  },
  icon: {
    width: 24,
    height: 24,
  },
  mainContentWrapper: {
    flexGrow: 1,
    margin: '12px 0px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
  },
  titleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1.5,
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    letterSpacing: '0.2px',
  },
  description: {
    fontSize: 14,
    lineHeight: 1.5,
    opacity: 0.9,
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    letterSpacing: '0.3px',
  },
  actionLink: {
    flexShrink: 0,
    textDecoration: 'none',
    height: 'none',
    lineHeight: 'none',
  },
  actionButton: {
    '&&': {
      flexShrink: 0,
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 600,
      textDecoration: 'underline',
      textUnderlineOffset: '4px',
      fontFamily: theme.fonts.body,
      minWidth: 'auto',
      whiteSpace: 'nowrap',
      padding: 0,
      letterSpacing: '0.2px',
      borderRadius: 0,
      minHeight: 'unset',

      '&&:hover,&&:active': {
        opacity: 0.8,
      },
    },
  },
  closeButton: {
    '&&': {
      flexShrink: 0,
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      transition: 'opacity 0.2s',
      minHeight: 'unset',
      width: 44,
      height: 44,
      marginTop: 2,
      marginRight: 2,

      '&&:hover': {
        borderRadius: '50%',
        opacity: 0.6,
      },
      '&&:active': {
        opacity: 0.6,
      },
    },
  },
  '@keyframes appear': {
    '0%': {
      opacity: 0,
      transform: 'translateY(50px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0px)',
    },
  },
  '@keyframes disappear': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
    },
  },
  neutral: {
    backgroundColor: '#343434',
    color: theme.colors.white,
  },
  positive: {
    backgroundColor: '#359C5F',
    color: theme.colors.white,
  },
  negative: {
    backgroundColor: '#EF423C',
    color: theme.colors.white,
  },
  closeButtonNeutral: {
    '&&:hover': {
      backgroundColor: '#4D4D4D',
    },
    '&&:active': {
      backgroundColor: '#696969',
    },
  },
  closeButtonPositive: {
    '&&:hover': {
      backgroundColor: '#2A643F',
    },
    '&&:active': {
      backgroundColor: '#22442E',
    },
  },
  closeButtonNegative: {
    '&&:hover': {
      backgroundColor: '#D3362B',
    },
    '&&:active': {
      backgroundColor: '#912C28',
    },
  },
};

export default injectSheet(styles)(Toast);
